import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import CaseFollowUpService from '../../../Api/services/case-follow-up.service';
import { useTranslation } from 'react-i18next';
import type { components } from '../../../Api/types/openapi';

type CaseStatusHistoryUSCISResponse = components['schemas']['CaseStatusHistoryUSCISResponse'];

interface QueryUSCISModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: (result: CaseStatusHistoryUSCISResponse) => void;
}

const QueryUSCISModal: React.FC<QueryUSCISModalProps> = ({
  isOpen,
  toggle,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [caseNumber, setCaseNumber] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [queryResult, setQueryResult] = useState<CaseStatusHistoryUSCISResponse | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCaseNumber('');
      setSuggestions([]);
      setQueryResult(null);
    }
  }, [isOpen]);

  const handleCaseNumberChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCaseNumber(value);

    // Buscar sugerencias si el número tiene al menos 3 caracteres
    if (value.length >= 3) {
      try {
        setLoadingSuggestions(true);
        const suggestionsData = await CaseFollowUpService.getLawyerCaseSuggestions(value);
        setSuggestions(suggestionsData || []);
      } catch (error) {
        // Silenciar errores de sugerencias
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setCaseNumber(suggestion);
    setSuggestions([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!caseNumber || caseNumber.trim().length === 0) {
      toast.error(t('El número de caso USCIS es requerido'));
      return;
    }

    try {
      setLoading(true);
      const result = await CaseFollowUpService.queryLawyerUSCIS({
        case_number_uscis: caseNumber.trim(),
      });
      setQueryResult(result);
      toast.success(t('Caso consultado exitosamente'));
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al consultar caso');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setCaseNumber('');
      setSuggestions([]);
      setQueryResult(null);
      toggle();
    }
  };

  const handleSave = () => {
    if (queryResult) {
      onSuccess(queryResult);
      handleClose();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>{t('Consultar USCIS')}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>{t('Número de Caso USCIS')} *</Label>
            <div className="position-relative">
              <Input
                type="text"
                value={caseNumber}
                onChange={handleCaseNumberChange}
                placeholder="Ej: MSC1234567890"
                required
                disabled={loading || !!queryResult}
              />
              {loadingSuggestions && (
                <div className="position-absolute" style={{ top: '5px', right: '10px' }}>
                  <Spinner size="sm" />
                </div>
              )}
              {suggestions.length > 0 && !queryResult && (
                <ListGroup className="position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                  {suggestions.map((suggestion, index) => (
                    <ListGroupItem
                      key={index}
                      action
                      onClick={() => handleSuggestionClick(suggestion)}
                      style={{ cursor: 'pointer' }}
                    >
                      {suggestion}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              )}
            </div>
          </FormGroup>

          {queryResult && (
            <div className="mt-3">
              <h6>{t('Resultado de la Consulta')}</h6>
              <div className="border p-3 rounded">
                {queryResult.comment && (
                  <p><strong>{t('Comentario')}:</strong> {queryResult.comment}</p>
                )}
                {queryResult.created_at && (
                  <p><strong>{t('Fecha')}:</strong> {new Date(queryResult.created_at).toLocaleString('es-ES')}</p>
                )}
                {queryResult.obj_uscis_response && (
                  <div>
                    <strong>{t('Respuesta USCIS')}:</strong>
                    <pre className="mt-2 p-2 bg-light rounded" style={{ fontSize: '0.9rem' }}>
                      {JSON.stringify(queryResult.obj_uscis_response, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={handleClose} disabled={loading}>
            {t('Cerrar')}
          </Btn>
          {!queryResult ? (
            <Btn color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {t('Consultando...')}
                </>
              ) : (
                t('Consultar')
              )}
            </Btn>
          ) : (
            <Btn color="success" onClick={handleSave}>
              {t('Guardar Resultado')}
            </Btn>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default QueryUSCISModal;

